// Condition DSL transforms, shared by custom-agent and notification-rule
// authoring clients and the agents trigger-normalization path. Two distinct
// entry points remain because they serve different inputs:
//
//   - conditionFromProxyWire / conditionToProxyWire — the UI's typed,
//     bidirectional transform between the form `Condition` (kind/operands)
//     and the proxy wire shape (op/children). Applies form defaults.
//   - normalizeConditionWire — the agents' tolerant normalizer for untyped
//     LLM tool output (accepts op|kind, children|operands, child|operand,
//     snake|camel leaves, symbol|word cmp, numeric-looking string→number
//     (non-numeric strings and booleans are kept), and
//     preserves unknown fields) into the proxy wire shape.
//
// They share the op / cmp / fn / window vocabulary from ./conditions.
//
// Builder-authored leaves carry the graph `:Tag.id` in `tag_id`; legacy
// records may carry a registry UUID. `tag_path` is read-only on inbound
// (proxy echo) and omitted on outbound writes.

import type {
  AggConditionLeaf,
  AlarmActiveConditionLeaf,
  Condition,
  ConditionCmp,
  ConditionFn,
  ConditionScalar,
  ConditionWindow,
  DeviationConditionLeaf,
  DeviationMetric,
  ExprConditionLeaf,
  ExprConditionSource,
  ExprSourceFn,
  LiveConditionLeaf
} from './conditions'
import {
  CMP_SYMBOL_TO_TOKEN,
  CONDITION_CMPS,
  CONDITION_FNS,
  EXPR_SOURCE_FNS,
  conditionSupportsDeadband
} from './conditions'

/** Local plain-object guard — keeps this package a dependency-light leaf. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// ─── Typed UI transform (form Condition ↔ proxy wire) ──────────────────────

function num(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    if (Number.isFinite(n)) return n
  }
  return 0
}

function optionalNonNegativeNum(v: unknown): number | undefined {
  if (v === undefined || v === null || v === '') return undefined
  const parsed = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : Number.NaN
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined
}

/** Preserve string / boolean live thresholds; coerce numeric-looking input. */
function asLiveValue(v: unknown): ConditionScalar {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string') return v
  return num(v)
}

function asCmp(v: unknown): ConditionCmp {
  const s = typeof v === 'string' ? v : ''
  return (CONDITION_CMPS as readonly string[]).includes(s) ? (s as ConditionCmp) : 'gt'
}

function asFn(v: unknown): ConditionFn {
  const s = typeof v === 'string' ? v : ''
  return (CONDITION_FNS as readonly string[]).includes(s) ? (s as ConditionFn) : 'avg'
}

// Unlike asCmp / asFn (closed enums that default an unknown value), windows are
// open-ended Go duration strings: the proxy accepts any duration (e.g. `30s`,
// `90s`, `2h30m`) and the toolspec documents windows that way, while
// `ConditionWindow` only enumerates the UI dropdown's convenience presets. So
// pass any non-empty string through verbatim — clobbering an off-preset window
// to `5m` would silently corrupt agent-authored conditions on edit — and only
// default an empty / missing window to `5m`. The cast is intentionally widening.
function asWindow(v: unknown): ConditionWindow {
  return typeof v === 'string' && v.trim() !== '' ? (v as ConditionWindow) : '5m'
}

function asDeviationMetric(v: unknown): DeviationMetric | undefined {
  if (v === 'zscore' || v === 'abs_diff' || v === 'pct_from_baseline') return v
  return undefined
}

/**
 * Extracts the optional human-authored description from a wire-format
 * condition node, dropping whitespace-only strings so a stray space in
 * a saved config doesn't lock the server out of its auto-generated
 * fallback.
 */
function asDescription(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined
  const trimmed = v.trim()
  return trimmed === '' ? undefined : trimmed
}

function asExprFn(v: unknown): ExprSourceFn | undefined {
  const s = typeof v === 'string' ? v : ''
  return (EXPR_SOURCE_FNS as readonly string[]).includes(s) ? (s as ExprSourceFn) : undefined
}

function asExprSources(raw: unknown): Record<string, ExprConditionSource> {
  if (!isPlainObject(raw)) return {}
  const out: Record<string, ExprConditionSource> = {}
  for (const [alias, src] of Object.entries(raw)) {
    if (!isPlainObject(src)) continue
    const fn = asExprFn(src.fn)
    const window =
      typeof src.window === 'string' && src.window.trim() !== '' ? src.window : undefined
    out[alias] = {
      tag_id: asTagId(src.tag_id),
      tag_path: asTagPath(src.tag_path),
      ...(fn ? { fn } : {}),
      ...(window ? { window } : {})
    }
  }
  return out
}

function exprSourcesToWire(
  sources: Record<string, ExprConditionSource>
): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {}
  for (const [alias, src] of Object.entries(sources)) {
    out[alias] = {
      tag_id: src.tag_id,
      ...(src.fn ? { fn: src.fn } : {}),
      ...(src.window ? { window: src.window } : {})
    }
  }
  return out
}

function normalizeExprSources(raw: unknown): Record<string, unknown> | undefined {
  if (!isPlainObject(raw)) return undefined
  const out: Record<string, unknown> = {}
  for (const [alias, src] of Object.entries(raw)) {
    if (!isPlainObject(src)) {
      out[alias] = src
      continue
    }
    const s: Record<string, unknown> = { ...src }
    assignTagIdentity(s)
    out[alias] = s
  }
  return out
}

/** Read a leaf's authored tag reference from the wire (proxy emits `tag_id`). */
function asTagId(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function readNonEmptyString(value: unknown): string {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : ''
}

/**
 * Map camelCase `tagId` → `tag_id`. Drop the free-text path when a tag id is
 * present; keep `tag_path` / `tagPath` as a fallback so a leaf that only
 * has a path still satisfies the proxy's `tag_id or tag_path` gate.
 */
function assignTagIdentity(node: Record<string, unknown>): void {
  if (typeof node.tagId === 'string' && node.tag_id === undefined) {
    node.tag_id = node.tagId
  }
  delete node.tagId
  const tagId = readNonEmptyString(node.tag_id)
  const path = readNonEmptyString(node.tag_path) || readNonEmptyString(node.tagPath)
  delete node.tagPath
  if (tagId) {
    node.tag_id = tagId
    delete node.tag_path
    return
  }
  delete node.tag_id
  if (path) node.tag_path = path
  else delete node.tag_path
}

/** Read the proxy's resolved read-only tag path, when present. */
function asTagPath(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined
}

/**
 * Adds `description` to a wire body when set. Centralized here so the
 * outgoing serialization in `conditionToProxyWire` can stay symmetric
 * with the incoming side without each branch repeating the conditional.
 */
function withDescription<T extends Record<string, unknown>>(
  base: T,
  description: string | undefined
): T {
  if (description !== undefined && description !== '') {
    return { ...base, description }
  }
  return base
}

function emptyLeafFallback(): LiveConditionLeaf {
  return { kind: 'live', tag_id: '', cmp: 'gt', value: 0 }
}

/**
 * Proxy-server and LLM tools emit `op`, `children`, and `child`.
 * Authoring clients expect `kind`, `operands`, and `operand`.
 */
export function conditionFromProxyWire(raw: unknown): Condition {
  if (raw === null || typeof raw !== 'object') {
    return { kind: 'and', operands: [emptyLeafFallback()] }
  }
  const o = raw as Record<string, unknown>
  const op = (typeof o.op === 'string' && o.op) || (typeof o.kind === 'string' && o.kind) || ''

  const description = asDescription(o.description)
  switch (op) {
    case 'live': {
      const leaf: LiveConditionLeaf = {
        kind: 'live',
        tag_id: asTagId(o.tag_id),
        tag_path: asTagPath(o.tag_path),
        cmp: asCmp(o.cmp),
        value: asLiveValue(o.value),
        deadband: optionalNonNegativeNum(o.deadband),
        description
      }
      return leaf
    }
    case 'agg': {
      const leaf: AggConditionLeaf = {
        kind: 'agg',
        tag_id: asTagId(o.tag_id),
        tag_path: asTagPath(o.tag_path),
        fn: asFn(o.fn),
        window: asWindow(o.window),
        cmp: asCmp(o.cmp),
        value: num(o.value),
        deadband: optionalNonNegativeNum(o.deadband),
        description
      }
      return leaf
    }
    case 'deviation': {
      const baselineWindow =
        typeof o.baseline_window === 'string' && o.baseline_window.trim() !== ''
          ? o.baseline_window
          : typeof o.window === 'string' && o.window.trim() !== ''
            ? o.window
            : '5m'
      const leaf: DeviationConditionLeaf = {
        kind: 'deviation',
        tag_id: asTagId(o.tag_id),
        tag_path: asTagPath(o.tag_path),
        window: asWindow(baselineWindow),
        cmp: asCmp(o.cmp),
        value: num(o.value),
        deadband: optionalNonNegativeNum(o.deadband),
        baseline_fn: asFn(o.baseline_fn),
        metric: asDeviationMetric(o.metric),
        description
      }
      return leaf
    }
    case 'alarm_active': {
      const leaf: AlarmActiveConditionLeaf = {
        kind: 'alarm_active',
        tag_id: asTagId(o.tag_id),
        tag_path: asTagPath(o.tag_path),
        alarm_name: typeof o.alarm_name === 'string' ? o.alarm_name : '',
        description
      }
      return leaf
    }
    case 'expr': {
      const leaf: ExprConditionLeaf = {
        kind: 'expr',
        expression: typeof o.expression === 'string' ? o.expression : '',
        sources: asExprSources(o.sources),
        cmp: asCmp(o.cmp),
        value: num(o.value),
        deadband: optionalNonNegativeNum(o.deadband),
        description
      }
      return leaf
    }
    case 'and':
    case 'or': {
      const rawKids = Array.isArray(o.children)
        ? o.children
        : Array.isArray(o.operands)
          ? o.operands
          : []
      const operands = rawKids.map((c) => conditionFromProxyWire(c))
      if (operands.length === 0) {
        operands.push(emptyLeafFallback())
      }
      return op === 'and'
        ? { kind: 'and', operands, description }
        : { kind: 'or', operands, description }
    }
    case 'not': {
      const inner = o.child ?? o.operand
      return {
        kind: 'not',
        operand: inner != null ? conditionFromProxyWire(inner) : emptyLeafFallback(),
        description
      }
    }
    default: {
      const leaf: LiveConditionLeaf = {
        kind: 'live',
        tag_id: asTagId(o.tag_id),
        tag_path: asTagPath(o.tag_path),
        cmp: asCmp(o.cmp),
        value: asLiveValue(o.value)
      }
      return leaf
    }
  }
}

/**
 * Inverse of {@link conditionFromProxyWire} for POST/PATCH bodies to the proxy.
 * Leaves emit `tag_id` only — the proxy resolves gateway/path/project from the
 * referenced registry tag. The read-only `tag_path` echoed back on GET/eval is
 * never sent on save.
 */
export function conditionToProxyWire(c: Condition): Record<string, unknown> {
  switch (c.kind) {
    case 'live':
      return withDescription(
        {
          op: 'live',
          tag_id: c.tag_id,
          cmp: c.cmp,
          value: c.value,
          ...(conditionSupportsDeadband(c) && c.deadband !== undefined
            ? { deadband: c.deadband }
            : {})
        },
        c.description
      )
    case 'agg':
      return withDescription(
        {
          op: 'agg',
          tag_id: c.tag_id,
          fn: c.fn,
          window: c.window,
          cmp: c.cmp,
          value: c.value,
          ...(conditionSupportsDeadband(c) && c.deadband !== undefined
            ? { deadband: c.deadband }
            : {})
        },
        c.description
      )
    case 'deviation':
      return withDescription(
        {
          op: 'deviation',
          tag_id: c.tag_id,
          baseline_fn: c.baseline_fn ?? 'avg',
          baseline_window: c.window,
          metric: c.metric ?? 'zscore',
          cmp: c.cmp,
          value: c.value,
          ...(conditionSupportsDeadband(c) && c.deadband !== undefined
            ? { deadband: c.deadband }
            : {})
        },
        c.description
      )
    case 'alarm_active':
      return withDescription(
        { op: 'alarm_active', tag_id: c.tag_id, alarm_name: c.alarm_name },
        c.description
      )
    case 'expr':
      return withDescription(
        {
          op: 'expr',
          expression: c.expression,
          sources: exprSourcesToWire(c.sources),
          cmp: c.cmp,
          value: c.value,
          ...(conditionSupportsDeadband(c) && c.deadband !== undefined
            ? { deadband: c.deadband }
            : {})
        },
        c.description
      )
    case 'and':
    case 'or':
      return withDescription(
        { op: c.kind, children: c.operands.map((x) => conditionToProxyWire(x)) },
        c.description
      )
    case 'not':
      return withDescription({ op: 'not', child: conditionToProxyWire(c.operand) }, c.description)
    default:
      return { op: 'live', tag_id: '', cmp: 'gt', value: 0 }
  }
}

// ─── Shared trigger-list condition mappers ─────────────────────────────────

/**
 * Map every trigger's condition from the proxy wire shape into the form
 * `Condition`. Custom-agent and notification-rule configs carry the same
 * `{ id?, description?, condition }[]` trigger list, so the only per-config
 * work is mapping each `condition` field; everything else on the trigger is
 * preserved. Generic over the trigger element so each caller keeps its own
 * trigger type.
 */
export function triggersFromProxyWire<T extends { condition: Condition }>(triggers: T[]): T[] {
  return triggers.map((t) => ({ ...t, condition: conditionFromProxyWire(t.condition as unknown) }))
}

/** Inverse of {@link triggersFromProxyWire} for POST/PATCH bodies to the proxy. */
export function triggersToProxyWire<T extends { condition: Condition }>(triggers: T[]): T[] {
  return triggers.map((t) => ({ ...t, condition: conditionToProxyWire(t.condition) as never }))
}

// ─── Tolerant agents normalizer (untyped LLM output → proxy wire) ───────────

/**
 * Maps common LLM / UI comparison spellings to the proxy DSL cmp tokens
 * (`gt`/`gte`/`lt`/`lte`/`eq`/`ne`). Returns the original string when it is
 * not a recognized symbol or token so the proxy validator can reject it.
 */
export function normalizeConditionCmp(cmp: unknown): string | undefined {
  if (typeof cmp !== 'string') return undefined
  const c = cmp.trim()
  const symbol = CMP_SYMBOL_TO_TOKEN[c]
  if (symbol) return symbol
  const lower = c.toLowerCase()
  if ((CONDITION_CMPS as readonly string[]).includes(lower)) return lower
  return c
}

/**
 * Normalize an untyped condition node (LLM tool output) into the proxy DSL:
 * `op` + `children`/`child` + snake_case leaves + cmp tokens. Tolerant of the
 * legacy `kind`/`operands`/`operand` spelling and camelCase leaf keys, and
 * preserves any fields it does not recognize so the proxy validator stays the
 * single source of truth for rejection.
 *
 * Maps `tagId` → `tag_id` and drops free-text `tag_path` / `tagPath` on write
 * when a tag id is present. Leaves that only have a path keep `tag_path` so
 * the proxy's `tag_id or tag_path` gate passes.
 */
export function normalizeConditionWire(node: unknown): unknown {
  if (!isPlainObject(node)) return node

  const n: Record<string, unknown> = { ...node }

  // `kind` and legacy `CondOp*` docs -> proxy expects `op`
  if (typeof n.kind === 'string' && n.op === undefined) {
    n.op = n.kind
  }
  delete n.kind

  // Authored tag reference. Accept camelCase; keep tag_path only when no id
  // was supplied (LLM follow-up parks often send tagPath alone).
  assignTagIdentity(n)

  const normalizedSources = normalizeExprSources(n.sources)
  if (normalizedSources) n.sources = normalizedSources

  if (typeof n.alarmName === 'string' && n.alarm_name === undefined) {
    n.alarm_name = n.alarmName
  }
  delete n.alarmName

  if (typeof n.baselineFn === 'string' && n.baseline_fn === undefined) {
    n.baseline_fn = n.baselineFn
  }
  delete n.baselineFn

  if (typeof n.baselineWindow === 'string' && n.baseline_window === undefined) {
    n.baseline_window = n.baselineWindow
  }
  delete n.baselineWindow

  // Composite: operands/operand (legacy DSL) -> children/child (proxy)
  if (Array.isArray(n.operands) && n.children === undefined) {
    n.children = n.operands.map((c) => normalizeConditionWire(c))
  }
  delete n.operands

  if (n.operand !== undefined && n.child === undefined) {
    n.child = normalizeConditionWire(n.operand)
  }
  delete n.operand

  if (Array.isArray(n.children)) {
    n.children = n.children.map((c) => normalizeConditionWire(c))
  }
  if (isPlainObject(n.child)) {
    n.child = normalizeConditionWire(n.child) as Record<string, unknown>
  }

  if (n.cmp !== undefined) {
    const nc = normalizeConditionCmp(n.cmp)
    if (nc) n.cmp = nc
  }

  if (typeof n.value === 'string' && n.value.trim() !== '' && !Number.isNaN(Number(n.value))) {
    n.value = Number(n.value)
  }
  if (
    typeof n.deadband === 'string' &&
    n.deadband.trim() !== '' &&
    !Number.isNaN(Number(n.deadband))
  ) {
    n.deadband = Number(n.deadband)
  }

  return n
}
